import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { api } from '../utils/api';
import { formatCurrency, formatDateShort } from '../utils/format';
import { Plus, CreditCard, Trash2, Pencil, FileText, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import FileUpload from '../components/FileUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CreditManagement() {
  const { currentUser } = useApp();
  const [krediler, setKrediler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingKredi, setEditingKredi] = useState(null);
  
  // Ödeme planı dialog
  const [showOdemePlaniDialog, setShowOdemePlaniDialog] = useState(false);
  const [selectedKredi, setSelectedKredi] = useState(null);
  
  const [addForm, setAddForm] = useState({
    bankaAdi: '',
    krediAdi: '',
    baslangicTarihi: '',
    toplamTutar: '',
    taksitSayisi: ''
  });

  useEffect(() => {
    loadKrediler();
  }, []);

  const loadKrediler = async () => {
    try {
      const res = await api.getKrediler();
      setKrediler(res.data);
    } catch (error) {
      console.error('Error loading credits:', error);
      toast.error('Krediler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!addForm.bankaAdi || !addForm.krediAdi || !addForm.baslangicTarihi || !addForm.toplamTutar || !addForm.taksitSayisi) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }

    try {
      await api.createKredi({
        bankaAdi: addForm.bankaAdi,
        krediAdi: addForm.krediAdi,
        baslangicTarihi: addForm.baslangicTarihi,
        toplamTutar: parseFloat(addForm.toplamTutar),
        taksitSayisi: parseInt(addForm.taksitSayisi)
      });
      toast.success('Kredi eklendi');
      setShowAddDialog(false);
      setAddForm({
        bankaAdi: '',
        krediAdi: '',
        baslangicTarihi: '',
        toplamTutar: '',
        taksitSayisi: ''
      });
      loadKrediler();
    } catch (error) {
      console.error('Error adding credit:', error);
      toast.error('Kredi eklenirken hata oluştu');
    }
  };

  const handleEdit = (kredi) => {
    setEditingKredi(kredi);
    setAddForm({
      bankaAdi: kredi.bankaAdi,
      krediAdi: kredi.krediAdi,
      baslangicTarihi: kredi.baslangicTarihi,
      toplamTutar: String(kredi.toplamTutar),
      taksitSayisi: String(kredi.taksitSayisi)
    });
    setShowEditDialog(true);
  };

  const handleUpdate = async () => {
    if (!addForm.bankaAdi || !addForm.krediAdi || !addForm.baslangicTarihi || !addForm.toplamTutar || !addForm.taksitSayisi) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }

    try {
      await api.updateKredi(editingKredi.id, {
        bankaAdi: addForm.bankaAdi,
        krediAdi: addForm.krediAdi,
        baslangicTarihi: addForm.baslangicTarihi,
        toplamTutar: parseFloat(addForm.toplamTutar),
        taksitSayisi: parseInt(addForm.taksitSayisi)
      });
      toast.success('Kredi güncellendi');
      setShowEditDialog(false);
      setEditingKredi(null);
      setAddForm({
        bankaAdi: '',
        krediAdi: '',
        baslangicTarihi: '',
        toplamTutar: '',
        taksitSayisi: ''
      });
      loadKrediler();
    } catch (error) {
      console.error('Error updating credit:', error);
      toast.error('Kredi güncellenirken hata oluştu');
    }
  };

  const handleDelete = async (krediId) => {
    if (!window.confirm('Bu krediyi ve tüm taksitlerini silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      await api.deleteKredi(krediId);
      toast.success('Kredi silindi');
      loadKrediler();
    } catch (error) {
      console.error('Error deleting credit:', error);
      toast.error('Kredi silinirken hata oluştu');
    }
  };

  const handleOpenOdemePlaniDialog = (kredi) => {
    setSelectedKredi(kredi);
    setShowOdemePlaniDialog(true);
  };

  const handleOdemePlaniUploadSuccess = (url) => {
    setSelectedKredi(prev => ({ ...prev, odemePlaniUrl: url }));
    loadKrediler();
  };

  const handleOdemePlaniDeleteSuccess = () => {
    setSelectedKredi(prev => ({ ...prev, odemePlaniUrl: null }));
    loadKrediler();
  };

  const renderForm = (isEdit = false) => (
    <div className="space-y-4 py-4">
      <div>
        <label className="block text-sm font-medium text-textPrimary mb-2">Banka Adı</label>
        <Input
          data-testid="banka-adi-input"
          value={addForm.bankaAdi}
          onChange={(e) => setAddForm({ ...addForm, bankaAdi: e.target.value })}
          placeholder="Örn: AKBANK"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-textPrimary mb-2">Kredi Adı</label>
        <Input
          data-testid="kredi-adi-input"
          value={addForm.krediAdi}
          onChange={(e) => setAddForm({ ...addForm, krediAdi: e.target.value })}
          placeholder="Örn: İşletme Kredisi"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-textPrimary mb-2">Başlangıç Tarihi</label>
        <Input
          type="date"
          data-testid="baslangic-tarihi-input"
          value={addForm.baslangicTarihi}
          onChange={(e) => setAddForm({ ...addForm, baslangicTarihi: e.target.value })}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-textPrimary mb-2">Toplam Tutar (₺)</label>
        <Input
          type="number"
          step="0.01"
          data-testid="toplam-tutar-input"
          value={addForm.toplamTutar}
          onChange={(e) => setAddForm({ ...addForm, toplamTutar: e.target.value })}
          placeholder="0.00"
          className="font-mono"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-textPrimary mb-2">Taksit Sayısı</label>
        <Input
          type="number"
          data-testid="taksit-sayisi-input"
          value={addForm.taksitSayisi}
          onChange={(e) => setAddForm({ ...addForm, taksitSayisi: e.target.value })}
          placeholder="12"
        />
      </div>
      <Button
        onClick={isEdit ? handleUpdate : handleAdd}
        data-testid={isEdit ? "update-credit-btn" : "submit-credit-btn"}
        className="w-full bg-primary hover:bg-primaryHover text-primaryForeground"
      >
        {isEdit ? 'Güncelle' : 'Kaydet'}
      </Button>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-textSecondary">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-8" data-testid="credit-management">
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="text-xl md:text-3xl font-heading font-bold text-textPrimary">Kredi Yönetimi</h1>
          <p className="text-xs md:text-base text-textSecondary mt-1 md:mt-2">Kredi ve taksit takibi</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-credit-btn"
              className="bg-primary hover:bg-primaryHover text-primaryForeground"
            >
              <Plus className="h-5 w-5 mr-2" />
              Yeni Kredi Ekle
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]" data-testid="add-credit-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Yeni Kredi Ekle</DialogTitle>
            </DialogHeader>
            {renderForm(false)}
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[500px]" data-testid="edit-credit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Kredi Düzenle</DialogTitle>
          </DialogHeader>
          {renderForm(true)}
        </DialogContent>
      </Dialog>

      {/* Credits List */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {krediler.map((kredi, index) => {
          const aylikTaksit = kredi.toplamTutar / kredi.taksitSayisi;
          return (
            <div key={kredi.id} className="bg-surface border border-border p-4 md:p-6 relative" data-testid={`kredi-card-${index}`}>
              {/* Action Buttons */}
              {currentUser?.rol === 'admin' && (
                <div className="absolute top-4 right-4 flex gap-2">
                  <button
                    onClick={() => handleEdit(kredi)}
                    data-testid={`edit-kredi-btn-${index}`}
                    className="text-primary hover:text-primaryHover p-1"
                    title="Düzenle"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(kredi.id)}
                    data-testid={`delete-kredi-btn-${index}`}
                    className="text-error hover:text-error/80 p-1"
                    title="Sil"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
              
              <div className="flex items-start justify-between mb-4">
                <div className="bg-primary p-3">
                  <CreditCard className="h-6 w-6 text-primaryForeground" strokeWidth={1.5} />
                </div>
                <span className="text-xs text-textSecondary">{formatDateShort(kredi.baslangicTarihi)}</span>
              </div>
              <h3 className="text-lg font-heading font-bold text-textPrimary mb-1">{kredi.krediAdi}</h3>
              <p className="text-sm text-textSecondary mb-4">{kredi.bankaAdi}</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-textSecondary">Toplam Tutar:</span>
                  <span className="font-mono font-bold text-textPrimary">{formatCurrency(kredi.toplamTutar)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-textSecondary">Taksit Sayısı:</span>
                  <span className="font-mono text-textPrimary">{kredi.taksitSayisi} ay</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-border">
                  <span className="text-textSecondary">Aylık Taksit:</span>
                  <span className="font-mono font-bold text-primary">{formatCurrency(aylikTaksit)}</span>
                </div>
                {/* Ödeme Planı Butonu */}
                <div className="pt-3 mt-3 border-t border-border">
                  <button
                    onClick={() => handleOpenOdemePlaniDialog(kredi)}
                    className={`w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded transition-colors ${
                      kredi.odemePlaniUrl 
                        ? 'bg-blue-50 text-blue-600 hover:bg-blue-100' 
                        : 'bg-surfaceSubtle text-textSecondary hover:bg-border'
                    }`}
                  >
                    {kredi.odemePlaniUrl ? (
                      <>
                        <Eye className="h-4 w-4" />
                        Ödeme Planını Görüntüle
                      </>
                    ) : (
                      <>
                        <FileText className="h-4 w-4" />
                        Ödeme Planı Ekle
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Ödeme Planı Dialog */}
      <Dialog open={showOdemePlaniDialog} onOpenChange={setShowOdemePlaniDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-heading">Kredi Ödeme Planı</DialogTitle>
            <DialogDescription>
              {selectedKredi?.krediAdi} için ödeme planı görseli yükleyin
            </DialogDescription>
          </DialogHeader>
          {selectedKredi && (
            <div className="py-4">
              <div className="mb-4 p-3 bg-surfaceSubtle rounded-lg">
                <p className="text-sm"><strong>Kredi:</strong> {selectedKredi.krediAdi}</p>
                <p className="text-sm"><strong>Banka:</strong> {selectedKredi.bankaAdi}</p>
                <p className="text-sm"><strong>Toplam:</strong> {formatCurrency(selectedKredi.toplamTutar)}</p>
              </div>
              <FileUpload
                endpoint={`/api/upload/kredi/${selectedKredi.id}`}
                currentUrl={selectedKredi.odemePlaniUrl}
                onUploadSuccess={handleOdemePlaniUploadSuccess}
                onDeleteSuccess={handleOdemePlaniDeleteSuccess}
                label="Ödeme Planı Yükle (PDF/Görsel)"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {krediler.length === 0 && (
        <div className="bg-surface border border-border p-12 text-center">
          <CreditCard className="h-16 w-16 text-textSecondary mx-auto mb-4" strokeWidth={1.5} />
          <p className="text-textSecondary">Henüz kredi kaydı yok</p>
        </div>
      )}
    </div>
  );
}
